/**
 * The photo editor.
 *
 * A small Lightroom, running entirely in the tab. That is not a compromise
 * forced by ambition — under end-to-end encryption the server holds no key, so
 * it *cannot* adjust a photograph even if we wanted it to. The browser is the
 * only place the pixels exist in the clear, which makes the constraint and the
 * right architecture the same thing.
 *
 * Two rules shape everything below:
 *
 *  - **The original is never touched.** Saving writes a new file beside it.
 *    A slider that could destroy the only copy of a shared photograph is not a
 *    slider, it is a hazard.
 *  - **What you see is what is saved.** One implementation of the maths serves
 *    the preview and the export, so the only difference between them is how
 *    many pixels went through it.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Button } from "../../components/ui/Button.js";
import { SegmentedControl } from "../../components/ui/SegmentedControl.js";
import { useToast } from "../../components/ui/Toast.js";
import {
  type Adjustments,
  NEUTRAL,
  describeAdjustments,
  isNeutral,
} from "./adjustments.js";
import {
  ASPECT_PRESETS,
  type CropRect,
  FULL_CROP,
  type Handle,
  IDENTITY,
  type Quarter,
  type Transform,
  centredCrop,
  displaySize,
  dragCrop,
  isIdentity,
  outputSize,
} from "./geometry.js";
import {
  drawBase,
  editedName,
  exportMime,
  loadBitmap,
  paintAdjusted,
  renderExport,
} from "./render.js";
import { LOOKS, matchingLook } from "./looks.js";
import "./PhotoEditor.css";

export interface EditableFile {
  id: string;
  name: string;
  mime: string;
}

export interface PhotoEditorProps {
  groupId: string;
  file: EditableFile;
  onClose: () => void;
  /** Handed the finished copy. The caller owns putting it into the queue. */
  onSave: (edited: File) => void;
}

type Tab = "crop" | "light" | "colour";

/**
 * The preview is capped rather than rendered at full resolution.
 *
 * Nothing is gained by pushing forty megapixels through a colour pass to fill a
 * box a thousand pixels wide, and on a phone it is the difference between a
 * slider that tracks and one that stutters. The export ignores this entirely.
 */
const PREVIEW_MAX_EDGE = 1400;

export function PhotoEditor({
  groupId,
  file,
  onClose,
  onSave,
}: PhotoEditorProps) {
  const { toast } = useToast();

  const [bitmap, setBitmap] = useState<ImageBitmap | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [proxied, setProxied] = useState(false);
  const [saving, setSaving] = useState(false);

  const [tab, setTab] = useState<Tab>("light");
  const [transform, setTransform] = useState<Transform>(IDENTITY);
  const [adjustments, setAdjustments] = useState<Adjustments>(NEUTRAL);
  const [aspect, setAspect] = useState<number | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const fitRef = useRef<HTMLDivElement>(null);
  const [frameBox, setFrameBox] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const baseRef = useRef<ImageData | null>(null);
  const rafRef = useRef<number | null>(null);

  const dirty = !isIdentity(transform) || !isNeutral(adjustments);

  /* ─────────────────────────── Loading ─────────────────────────────────── */

  useEffect(() => {
    let live = true;
    let loaded: ImageBitmap | null = null;

    void (async () => {
      try {
        /*
         * The original, so the export is the real thing. Falling back to the
         * large preview keeps the editor usable for a file whose original has
         * gone missing — the interface says so rather than silently saving a
         * downscaled copy as if it were full resolution.
         */
        let usedProxy = false;
        let result: ImageBitmap;
        try {
          result = await loadBitmap(groupId, file.id, "original", file.mime);
        } catch {
          usedProxy = true;
          result = await loadBitmap(
            groupId,
            file.id,
            "preview_hq",
            "image/webp",
          );
        }

        if (!live) {
          result.close();
          return;
        }
        loaded = result;
        setProxied(usedProxy);
        setBitmap(result);
      } catch (error) {
        if (!live) return;
        setLoadError(
          error instanceof Error
            ? error.message
            : "That photo could not be opened for editing.",
        );
      }
    })();

    return () => {
      live = false;
      // Decoded frames live outside the JavaScript heap; dropping the reference
      // is not enough to give the memory back.
      loaded?.close();
    };
  }, [groupId, file.id, file.mime]);

  /* ─────────────────────────── Rendering ───────────────────────────────── */

  /*
   * In the crop tab the whole frame is shown with a rectangle over it, because
   * cropping against an already-cropped picture would hide the very thing being
   * chosen. Everywhere else the canvas shows the result.
   */
  const previewTransform = useMemo<Transform>(
    () => (tab === "crop" ? { ...transform, crop: FULL_CROP } : transform),
    [tab, transform],
  );

  // The base changes only when the geometry does, which is rarely.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !bitmap) return;
    baseRef.current = drawBase(
      canvas,
      bitmap,
      previewTransform,
      PREVIEW_MAX_EDGE,
    );
    paintAdjusted(canvas, baseRef.current, adjustments);
    // `adjustments` is applied here but deliberately not a dependency: the
    // effect below owns repainting for it, and listing it would redraw the
    // base on every frame of a slider drag.
  }, [bitmap, previewTransform]);

  // Colour repaints from the cached base, coalesced to one per frame.
  useEffect(() => {
    const canvas = canvasRef.current;
    const base = baseRef.current;
    if (!canvas || !base) return;

    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      paintAdjusted(canvas, base, adjustments);
    });

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [adjustments]);

  /*
   * The painted box, measured rather than described.
   *
   * A canvas has an intrinsic size, so `max-block-size: 100%` looks like it
   * should fit it to the stage — but the percentage resolves against the frame,
   * whose height comes from the canvas, and a circular definition resolves to
   * nothing. The picture rendered at its full pixel height and ran off the
   * screen. Measuring is not a workaround here: the crop overlay is positioned
   * in percentages of this box, so it has to be exactly the painted picture,
   * and an exact number is the only thing that guarantees that.
   */
  useLayoutEffect(() => {
    const fit = fitRef.current;
    if (!fit || !bitmap) return;

    const shown = outputSize(previewTransform, bitmap.width, bitmap.height);
    const aspect = shown.width / shown.height;

    const measure = () => {
      const available = { width: fit.clientWidth, height: fit.clientHeight };
      if (available.width === 0 || available.height === 0) return;

      // Contain: whichever axis runs out first decides.
      const width = Math.min(available.width, available.height * aspect);
      setFrameBox({ width, height: width / aspect });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(fit);
    return () => observer.disconnect();
  }, [bitmap, previewTransform]);

  /* ─────────────────────────── Cropping ────────────────────────────────── */

  const display = bitmap
    ? displaySize(bitmap.width, bitmap.height, transform.rotate)
    : { width: 1, height: 1 };

  const startDrag = useCallback(
    (handle: Handle) => (event: React.PointerEvent) => {
      const frame = frameRef.current;
      if (!frame) return;

      event.preventDefault();
      event.stopPropagation();
      const box = frame.getBoundingClientRect();
      let lastX = event.clientX;
      let lastY = event.clientY;

      const onMove = (move: PointerEvent) => {
        const dx = (move.clientX - lastX) / box.width;
        const dy = (move.clientY - lastY) / box.height;
        lastX = move.clientX;
        lastY = move.clientY;
        setTransform((current) => ({
          ...current,
          crop: dragCrop(
            current.crop,
            handle,
            dx,
            dy,
            aspect,
            display.width,
            display.height,
          ),
        }));
      };

      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [aspect, display.width, display.height],
  );

  const chooseAspect = useCallback(
    (value: number | null) => {
      setAspect(value);
      if (value === null) return;
      setTransform((current) => {
        const size = displaySize(
          bitmap?.width ?? 1,
          bitmap?.height ?? 1,
          current.rotate,
        );
        return {
          ...current,
          crop: centredCrop(value, size.width, size.height),
        };
      });
    },
    [bitmap],
  );

  /*
   * Turning the picture turns the crop with it, rather than throwing it away.
   * A crop chosen before a rotation is still the same part of the photograph
   * afterwards, and resetting it would feel like the app forgetting.
   */
  const rotate = useCallback((direction: 1 | -1) => {
    setTransform((current) => {
      const next = (((current.rotate + direction * 90) % 360) + 360) % 360;
      const crop = current.crop;
      const turned: CropRect =
        direction === 1
          ? {
              x: 1 - crop.y - crop.height,
              y: crop.x,
              width: crop.height,
              height: crop.width,
            }
          : {
              x: crop.y,
              y: 1 - crop.x - crop.width,
              width: crop.height,
              height: crop.width,
            };
      return { ...current, rotate: next as Quarter, crop: turned };
    });
  }, []);

  /* ─────────────────────────── Saving ──────────────────────────────────── */

  const save = useCallback(async () => {
    if (!bitmap) return;
    setSaving(true);
    try {
      const mime = exportMime(file.mime);
      const blob = await renderExport(bitmap, transform, adjustments, mime);
      const name = editedName(file.name, mime);
      onSave(new File([blob], name, { type: mime }));
    } catch (error) {
      toast(
        error instanceof Error
          ? error.message
          : "The edited photo could not be saved.",
        { variant: "danger" },
      );
      setSaving(false);
    }
  }, [bitmap, transform, adjustments, file.mime, file.name, onSave, toast]);

  const requestClose = useCallback(() => {
    if (dirty && !window.confirm("Discard these edits?")) return;
    onClose();
  }, [dirty, onClose]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        requestClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [requestClose]);

  /* ─────────────────────────── Interface ───────────────────────────────── */

  const result = bitmap
    ? outputSize(transform, bitmap.width, bitmap.height)
    : null;
  const changes = describeAdjustments(adjustments);
  const activeLook = matchingLook(adjustments);

  return createPortal(
    <div
      className="fc-editor"
      role="dialog"
      aria-modal="true"
      aria-label={`Editing ${file.name}`}
    >
      <header className="fc-editor__bar">
        <Button variant="ghost" onClick={requestClose}>
          Cancel
        </Button>

        <div className="fc-editor__title">
          <span className="fc-editor__name">{file.name}</span>
          {result && (
            <span className="fc-editor__size fc-tabular">
              {result.width} × {result.height}
              {proxied && " · editing a smaller copy"}
            </span>
          )}
        </div>

        <div className="fc-editor__actions">
          {dirty && (
            <Button
              variant="ghost"
              onClick={() => {
                setTransform(IDENTITY);
                setAdjustments(NEUTRAL);
                setAspect(null);
              }}
            >
              Reset
            </Button>
          )}
          <Button
            variant="primary"
            loading={saving}
            disabled={!bitmap || !dirty}
            onClick={() => void save()}
          >
            Save a copy
          </Button>
        </div>
      </header>

      <div className="fc-editor__stage">
        {loadError ? (
          <p className="fc-editor__error">{loadError}</p>
        ) : !bitmap ? (
          <p className="fc-editor__loading">Opening the full-size photo…</p>
        ) : (
          <div className="fc-editor__fit" ref={fitRef}>
            <div
              className="fc-editor__frame"
              ref={frameRef}
              style={
                frameBox
                  ? {
                      inlineSize: `${frameBox.width}px`,
                      blockSize: `${frameBox.height}px`,
                    }
                  : { visibility: "hidden" }
              }
            >
              <canvas ref={canvasRef} className="fc-editor__canvas" />

              {tab === "crop" && (
                <div
                  className="fc-editor__crop"
                  style={{
                    left: `${transform.crop.x * 100}%`,
                    top: `${transform.crop.y * 100}%`,
                    width: `${transform.crop.width * 100}%`,
                    height: `${transform.crop.height * 100}%`,
                  }}
                  onPointerDown={startDrag("move")}
                >
                  {/* Rule of thirds, the reason anybody opens a crop tool. */}
                  <span className="fc-editor__thirds" aria-hidden="true" />
                  {(
                    ["nw", "ne", "sw", "se", "n", "s", "w", "e"] as Handle[]
                  ).map((handle) => (
                    <span
                      key={handle}
                      className="fc-editor__handle"
                      data-handle={handle}
                      onPointerDown={startDrag(handle)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="fc-editor__panel">
        <SegmentedControl
          className="fc-editor__tabs"
          aria-label="Editing tools"
          value={tab}
          onChange={setTab}
          options={[
            { value: "crop", label: "Crop" },
            { value: "light", label: "Light" },
            { value: "colour", label: "Colour" },
          ]}
        />

        {tab === "crop" ? (
          <div className="fc-editor__controls">
            <div
              className="fc-editor__chips"
              role="radiogroup"
              aria-label="Shape"
            >
              {ASPECT_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  role="radio"
                  aria-checked={aspect === preset.value}
                  data-active={aspect === preset.value || undefined}
                  className="fc-editor__chip"
                  onClick={() => chooseAspect(preset.value)}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <div className="fc-editor__chips">
              <button
                type="button"
                className="fc-editor__chip"
                onClick={() => rotate(-1)}
              >
                Turn left
              </button>
              <button
                type="button"
                className="fc-editor__chip"
                onClick={() => rotate(1)}
              >
                Turn right
              </button>
              <button
                type="button"
                className="fc-editor__chip"
                data-active={transform.flipH || undefined}
                onClick={() =>
                  setTransform((current) => ({
                    ...current,
                    flipH: !current.flipH,
                  }))
                }
              >
                Mirror
              </button>
            </div>
          </div>
        ) : tab === "light" ? (
          <div className="fc-editor__controls">
            <Slider
              label="Exposure"
              value={adjustments.exposure}
              onChange={(exposure) =>
                setAdjustments((a) => ({ ...a, exposure }))
              }
            />
            <Slider
              label="Contrast"
              value={adjustments.contrast}
              onChange={(contrast) =>
                setAdjustments((a) => ({ ...a, contrast }))
              }
            />
            <Slider
              label="Highlights"
              value={adjustments.highlights}
              onChange={(highlights) =>
                setAdjustments((a) => ({ ...a, highlights }))
              }
            />
            <Slider
              label="Shadows"
              value={adjustments.shadows}
              onChange={(shadows) => setAdjustments((a) => ({ ...a, shadows }))}
            />
          </div>
        ) : (
          <div className="fc-editor__controls">
            <div
              className="fc-editor__chips"
              role="radiogroup"
              aria-label="Looks"
            >
              {LOOKS.map((look) => (
                <button
                  key={look.name}
                  type="button"
                  role="radio"
                  aria-checked={activeLook === look.name}
                  data-active={activeLook === look.name || undefined}
                  className="fc-editor__chip"
                  onClick={() => setAdjustments(look.adjustments)}
                >
                  {look.name}
                </button>
              ))}
            </div>
            <Slider
              label="Saturation"
              value={adjustments.saturation}
              onChange={(saturation) =>
                setAdjustments((a) => ({ ...a, saturation }))
              }
              disabled={adjustments.monochrome}
            />
            <Slider
              label="Warmth"
              value={adjustments.warmth}
              onChange={(warmth) => setAdjustments((a) => ({ ...a, warmth }))}
            />
            <label className="fc-editor__switch">
              <input
                type="checkbox"
                checked={adjustments.monochrome}
                onChange={(event) =>
                  setAdjustments((a) => ({
                    ...a,
                    monochrome: event.target.checked,
                  }))
                }
              />
              Black and white
            </label>
          </div>
        )}

        {changes.length > 0 && (
          <p className="fc-editor__changes">Changed: {changes.join(", ")}</p>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * One adjustment.
 *
 * Double-click returns it to neutral, which is the convention every editor
 * shares and the only way back to exactly zero with a mouse.
 */
function Slider({
  label,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}) {
  return (
    <label className="fc-editor__slider" data-disabled={disabled || undefined}>
      <span className="fc-editor__slider-label">
        {label}
        <span className="fc-tabular" data-zero={value === 0 || undefined}>
          {value > 0 ? `+${value}` : value}
        </span>
      </span>
      <input
        type="range"
        min={-100}
        max={100}
        step={1}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(Number(event.target.value))}
        onDoubleClick={() => onChange(0)}
      />
    </label>
  );
}
